import { useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, UserPlus, X } from "@phosphor-icons/react";

/**
 * Lista de jugadores asignados a una ronda específica de una cancha.
 * Permite añadir registrados o invitados.
 */
export default function RoundRoster({ reta, courtIdx, roundNum, court, players, canEdit, onChanged }) {
  const [openAdd, setOpenAdd] = useState(false);
  const [mode, setMode] = useState("registered"); // registered | guest
  const [selectedUserId, setSelectedUserId] = useState("");
  const [guestName, setGuestName] = useState("");
  const [saving, setSaving] = useState(false);

  const rosters = court.round_rosters || {};
  const roster = rosters[String(roundNum)] || [];

  const availablePlayers = useMemo(() => {
    const inRoster = new Set(roster.map((p) => p.user_id).filter(Boolean));
    return (players || []).filter((p) => !inRoster.has(p.id));
  }, [players, roster]);

  const addPlayer = async () => {
    let payload = {};
    if (mode === "registered") {
      if (!selectedUserId) { toast.error("Selecciona un jugador"); return; }
      const u = (players || []).find((p) => p.id === selectedUserId);
      payload = { user_id: selectedUserId, name: u?.name || "Jugador", is_guest: false };
    } else {
      const nm = guestName.trim();
      if (!nm) { toast.error("Nombre requerido"); return; }
      payload = { name: nm, is_guest: true };
    }
    setSaving(true);
    try {
      await api.post(`/retas/multi/${reta.id}/courts/${courtIdx}/rounds/${roundNum}/roster`, payload);
      toast.success(`Agregado a Ronda ${roundNum}`);
      setSelectedUserId("");
      setGuestName("");
      setOpenAdd(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al agregar");
    } finally {
      setSaving(false);
    }
  };

  const removePlayer = async (p) => {
    if (!window.confirm(`Quitar ${p.name} de Ronda ${roundNum}?`)) return;
    try {
      const body = p.user_id ? { user_id: p.user_id } : { name: p.name };
      await api.request({ method: "DELETE", url: `/retas/multi/${reta.id}/courts/${courtIdx}/rounds/${roundNum}/roster`, data: body });
      toast.success("Removido");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="mt-1.5 mb-1.5">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
        <span className="text-[9px] tracking-[0.2em] uppercase text-gray-500 font-bold">Jugadores de la ronda ({roster.length})</span>
        {canEdit && (
          <button onClick={() => setOpenAdd(true)} className="text-[9px] text-[#CCFF00] hover:underline uppercase tracking-wide flex items-center gap-0.5" data-testid={`round-roster-add-${reta.id}-${courtIdx}-${roundNum}`}>
            <UserPlus size={10} /> + jugador
          </button>
        )}
      </div>
      {roster.length === 0 ? (
        <div className="text-[9px] text-gray-600 italic">Sin jugadores asignados a esta ronda (se usan los de la cancha).</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {roster.map((p, i) => (
            <span key={(p.user_id || p.name) + i} className={`px-1.5 py-0.5 text-[10px] flex items-center gap-1 rounded ${p.is_guest ? "bg-yellow-500/10 text-yellow-300 border border-yellow-500/20" : "bg-white/5 text-white border border-white/10"}`} data-testid={`round-roster-chip-${reta.id}-${courtIdx}-${roundNum}-${i}`}>
              {p.name}{p.is_guest ? " (inv)" : ""}
              {canEdit && (
                <button onClick={() => removePlayer(p)} className="hover:text-red-400 ml-0.5"><X size={9} /></button>
              )}
            </span>
          ))}
        </div>
      )}

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase">Agregar jugador a Ronda {roundNum}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setMode("registered")} className={`text-[10px] uppercase font-bold ${mode === "registered" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-white/15"}`}>Registrado</Button>
              <Button size="sm" variant="outline" onClick={() => setMode("guest")} className={`text-[10px] uppercase font-bold ${mode === "guest" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-white/15"}`}>Invitado</Button>
            </div>
            {mode === "registered" ? (
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15"><SelectValue placeholder="Seleccionar jugador" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-60">
                  {availablePlayers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Nombre del invitado" className="bg-[#0A0A0A] border-white/15" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAdd(false)} className="border-white/15">Cancelar</Button>
            <Button onClick={addPlayer} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold">
              <Plus size={14} className="mr-1" /> Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
